import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useGetIdentity, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { phone10, telHref } from "../contactLinks";
import {
  fmtDuration,
  startCallTimer,
  stopCallTimer,
  useCallTimer,
} from "../calls/callTimer";
import { MobilePage } from "../MobilePage";
import { OfferDot, StagePill } from "../ui";
import { offerColor, useOffers } from "../useOffers";
import { LossDialog, type LossTarget } from "../pipeline/LossDialog";
import { DictateButton } from "../myday/Dictate";
import {
  CLOSING,
  COMMITMENTS,
  logActivity,
  OUTCOMES,
} from "../myday/logActivity";
import { useAgendaUrl } from "../templates/useTemplateFor";
import { HelpTooltip } from "../help/HelpTooltip";
import { SendPreview } from "../templates/SendPreview";
import { TranscriptConsentCard } from "../calls/TranscriptConsentCard";
import { AudioCaptureCard } from "../calls/AudioCaptureCard";
import { useCallScript } from "./callScript";
import { GENERIC_PLAY, PLAYBOOK, useBestHour } from "./playbook";
import { ThermoBar } from "../thermo/thermo";

type QueueRow = {
  deal_id: number;
  key: string;
  offer_id: number;
  offer_slug: string;
  offer_name: string;
  stage: string;
  company_id: number;
  company_name: string;
  city: string | null;
  sector: string | null;
  contact_id: number | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  thermo: number;
};

type SessionState = {
  id: number; // call_sessions.id
  queue: QueueRow[];
  i: number;
  results: Record<number, string>; // deal_id → resultado
  durations: Record<number, number>; // deal_id → segundos hablados (Bloque L)
  startedAt: number;
  ended?: boolean;
};

const STORE = "raftel.call_session";
// Orden del prototipo para la sesión (los más usados al llamar)
const SESSION_OUTCOMES = [
  "hablo_decisor",
  "demo_agendada",
  "respondio",
  "contesto_otro",
  "sin_respuesta",
  "buzon",
  "no_interesado",
  "numero_incorrecto",
].map((v) => OUTCOMES.find((o) => o.value === v)!);
const SESSION_NEXT = [
  { days: 1, label: "Mañana" },
  { days: 3, label: "+3 días" },
  { days: 7, label: "+1 semana" },
];
const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;
const talked = (r: string) =>
  r === "Hablé con decisor" || r === "Demo agendada";

const load = (): SessionState | null => {
  try {
    const s = JSON.parse(localStorage.getItem(STORE) ?? "null");
    return s && !s.ended ? { durations: {}, ...s } : null;
  } catch {
    return null;
  }
};

/** Sesión de llamadas (PRD C.4). */
export const CallSession = () => {
  const [state, setState] = useState<SessionState | null>(load);
  const [starting, setStarting] = useState(false);
  const [summary, setSummary] = useState<SessionState | null>(null);
  const { identity } = useGetIdentity();
  const notify = useNotify();

  useEffect(() => {
    if (state) localStorage.setItem(STORE, JSON.stringify(state));
  }, [state]);

  const start = async () => {
    setStarting(true);
    const sb = getSupabaseClient();
    // Hasta 20: una por empresa, sin No contactar, las más calientes primero (vista call_queue).
    const { data: queue, error } = await sb
      .from("call_queue")
      .select("*")
      .limit(20);
    if (error) {
      notify(error.message, { type: "error" });
      setStarting(false);
      return;
    }
    if (!queue?.length) {
      setStarting(false);
      setSummary({
        id: 0,
        queue: [],
        i: 0,
        results: {},
        durations: {},
        startedAt: Date.now(),
        ended: true,
      });
      return;
    }
    const { data: row } = await sb
      .from("call_sessions")
      .insert({ sales_id: identity?.id })
      .select("id")
      .single();
    setSummary(null);
    setState({
      id: row!.id,
      queue: queue as QueueRow[],
      i: 0,
      results: {},
      durations: {},
      startedAt: Date.now(),
    });
    setStarting(false);
  };

  const finish = async (s: SessionState) => {
    const results = Object.values(s.results);
    await getSupabaseClient()
      .from("call_sessions")
      .update({
        ended_at: new Date().toISOString(),
        calls: results.filter((r) => r !== "Saltada").length,
        decisors: results.filter(talked).length,
        demos: results.filter((r) => r === "Demo agendada").length,
      })
      .eq("id", s.id);
    localStorage.removeItem(STORE);
    setState(null);
    setSummary({ ...s, ended: true });
  };

  return (
    <MobilePage title="Sesión de llamadas">
      {state ? (
        <Running state={state} setState={setState} finish={finish} />
      ) : summary ? (
        <Summary s={summary} onRestart={start} starting={starting} />
      ) : (
        <Intro onStart={start} starting={starting} />
      )}
    </MobilePage>
  );
};

const Intro = ({
  onStart,
  starting,
}: {
  onStart: () => void;
  starting: boolean;
}) => (
  <Card className="max-w-2xl gap-3 p-5">
    <h1 className="text-2xl font-extrabold tracking-tight">
      Sesión de llamadas
    </h1>
    <p className="text-muted-foreground">
      Hasta 20 oportunidades abiertas en fila (una por empresa, sin No
      contactar), con el guion al lado y el registro en un toque. Las más
      calientes (termómetro) van primero.
    </p>
    <div className="inline-flex items-center gap-1">
      <Button className="self-start" onClick={onStart} disabled={starting}>
        <Phone />
        {starting ? "Preparando la fila…" : "Iniciar sesión"}
      </Button>
      <HelpTooltip topic="calls.start" />
    </div>
  </Card>
);

const Summary = ({
  s,
  onRestart,
  starting,
}: {
  s: SessionState;
  onRestart: () => void;
  starting: boolean;
}) => {
  const results = Object.values(s.results);
  const mins = Math.max(1, Math.round((Date.now() - s.startedAt) / 60000));
  const talkedSeconds = Object.values(s.durations ?? {});
  const longest = talkedSeconds.length ? Math.max(...talkedSeconds) : null;
  const totalTalkedSec = talkedSeconds.reduce((a, b) => a + b, 0);
  return (
    <Card className="max-w-2xl gap-3 p-5">
      <h1 className="text-2xl font-extrabold tracking-tight">
        {s.queue.length ? "Resumen de la sesión" : "No hay a quién llamar"}
      </h1>
      {s.queue.length ? (
        <p>
          {plural(results.filter((r) => r !== "Saltada").length, "llamada")} en{" "}
          {plural(mins, "minuto")} · {results.filter(talked).length} con decisor
          ·{" "}
          <b>
            {plural(
              results.filter((r) => r === "Demo agendada").length,
              "demo agendada",
              "demos agendadas",
            )}
          </b>
          .
          {talkedSeconds.length > 0 && (
            <>
              {" "}
              Tiempo hablado: {fmtDuration(totalTalkedSec)} · más larga:{" "}
              {fmtDuration(longest!)}.
            </>
          )}
        </p>
      ) : (
        <p className="text-muted-foreground">
          No tienes oportunidades abiertas para llamar. Pasa empresas al CRM
          desde el Directorio.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline">
          <Link to="/">Volver a Mi día</Link>
        </Button>
        <Button onClick={onRestart} disabled={starting}>
          Nueva sesión
        </Button>
      </div>
    </Card>
  );
};

const Running = ({
  state,
  setState,
  finish,
}: {
  state: SessionState;
  setState: (s: SessionState) => void;
  finish: (s: SessionState) => void;
}) => {
  const isMobile = useIsMobile();
  const { data: offers } = useOffers();
  const agenda = useAgendaUrl();
  const bestHour = useBestHour();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [next, setNext] = useState(3);
  const [commitment, setCommitment] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loss, setLoss] = useState<LossTarget | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [, tick] = useState(0);
  const timer = useCallTimer();

  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30000); // minutos en vivo
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (timer.active) setDuration(timer.seconds);
  }, [timer.active, timer.seconds]);

  const { queue, i, results, durations } = state;
  const d = queue[i];
  const script = useCallScript(d?.offer_id ?? null, {
    nombre: d?.first_name,
    empresa: d?.company_name,
    giro: d?.sector?.toLowerCase(), // aproximación: sector, no hay "actividad" en la fila de cola
    municipio: d?.city,
    remitente: identity?.fullName?.split(" ")[0],
    agenda,
  });
  const values = Object.values(results);
  const mins = Math.max(1, Math.round((Date.now() - state.startedAt) / 60000));
  const talkedSeconds = Object.values(durations);
  const totalTalkedMin = Math.round(
    talkedSeconds.reduce((a, b) => a + b, 0) / 60,
  );
  const avgTalkedSec = talkedSeconds.length
    ? Math.round(
        talkedSeconds.reduce((a, b) => a + b, 0) / talkedSeconds.length,
      )
    : null;

  const go = (
    idx: number,
    res?: Record<number, string>,
    dur?: Record<number, number>,
  ) => {
    setOutcome(null);
    setNote("");
    setNext(3);
    setCommitment(null);
    setDuration(null);
    const s = {
      ...state,
      i: idx,
      results: res ?? results,
      durations: dur ?? durations,
    };
    if (idx >= queue.length) finish(s);
    else setState(s);
  };

  if (!d) return null;
  const offer = offers?.find((o) => o.id === d.offer_id);
  const play = PLAYBOOK[d.offer_slug] ?? GENERIC_PLAY;
  const waTarget = {
    template_id: null,
    offer_slug: d.offer_slug,
    company_name: d.company_name,
    first_name: d.first_name,
    giro: null,
    city: d.city,
  };
  const me = identity?.fullName?.split(" ")[0] ?? "";
  const ev = `Trabajo con ${play.proof} de ${d.city ?? "su zona"} y casi todas tenían el mismo problema`;

  const save = async () => {
    if (!outcome) return notify("Elige un resultado", { type: "warning" });
    if (!d.contact_id)
      return notify("Esta empresa no tiene contacto: agrégalo en su ficha", {
        type: "warning",
      });
    setSaving(true);
    try {
      await logActivity({
        contactId: d.contact_id!,
        dealId: d.deal_id,
        channel: "llamada",
        outcome,
        note,
        nextDays: CLOSING.includes(outcome) ? 0 : next,
        nextTaskType: "call",
        nextTaskText: "Llamada de seguimiento",
        close: { dealTasksDueToday: true },
        deal: offer ? { stage: d.stage, stages: offer.stages } : undefined,
        durationSeconds: duration,
        commitment,
        templateId: script?.templateId ?? null,
      });
      const label = OUTCOMES.find((o) => o.value === outcome)!.label;
      const res = { ...results, [d.deal_id]: label };
      const dur =
        duration != null ? { ...durations, [d.deal_id]: duration } : durations;
      // guarda el avance en call_sessions aunque la sesión no termine
      await getSupabaseClient()
        .from("call_sessions")
        .update({
          calls: Object.values(res).filter((r) => r !== "Saltada").length,
          decisors: Object.values(res).filter(talked).length,
          demos: Object.values(res).filter((r) => r === "Demo agendada").length,
        })
        .eq("id", state.id);
      [
        "my_day",
        "deal_board",
        "funnel_by_offer",
        "weekly_goal",
        "done_today",
        "thermo_alerts",
      ].forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
      notify(`Guardado: ${label}`, { type: "success" });
      go(i + 1, res, dur);
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setSaving(false);
  };

  const trySave = () => {
    if (timer.active) stopCallTimer();
    if (
      duration != null &&
      duration > 90 * 60 &&
      !window.confirm(
        `${fmtDuration(duration)} es una llamada muy larga (posible olvido). ¿Guardar de todas formas?`,
      )
    )
      return;
    if (outcome === "no_interesado" && offer) {
      setLoss({
        id: d.deal_id,
        key: d.key,
        company_name: d.company_name,
        offer_id: d.offer_id,
      });
    } else save();
  };

  const head = (
    <Card className="flex-row flex-wrap items-center gap-3 p-3">
      <b>
        Llamada {Math.min(i + 1, queue.length)} de {queue.length}
      </b>
      <div className="h-1.5 min-w-32 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${(i / queue.length) * 100}%` }}
        />
      </div>
      <span className="rounded-full border px-2.5 py-0.5 text-xs font-semibold">
        {values.filter(talked).length} con decisor
      </span>
      <span className="rounded-full border px-2.5 py-0.5 text-xs font-semibold">
        {values.filter((r) => r === "Demo agendada").length} demos
      </span>
      <span className="text-sm text-muted-foreground">{mins} min</span>
      {talkedSeconds.length > 0 && (
        <span className="text-sm text-muted-foreground">
          {totalTalkedMin} min hablados · {fmtDuration(avgTalkedSec!)} promedio
        </span>
      )}
      <Button size="sm" variant="outline" onClick={() => finish({ ...state })}>
        Terminar sesión
      </Button>
    </Card>
  );

  const fila = (
    <Card className="gap-1 p-2 md:max-h-[70vh] md:overflow-y-auto">
      <h3 className="px-2 py-1 font-extrabold">Fila</h3>
      {queue.map((q, idx) => (
        <button
          key={q.deal_id}
          onClick={() => go(idx)}
          className={cn(
            "grid grid-cols-[22px_1fr] gap-2 rounded-lg p-2 text-left hover:bg-secondary",
            idx === i && "bg-accent",
          )}
        >
          <span className="font-bold text-muted-foreground">{idx + 1}</span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-bold">
              {q.company_name}
            </span>
            <span className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              {q.key} · {results[q.deal_id] ?? q.offer_name}
              {q.thermo != null && <ThermoBar value={q.thermo} mini />}
            </span>
          </span>
        </button>
      ))}
    </Card>
  );

  const phone = d.phone ? phone10(d.phone) : null;
  const tel = telHref(d.phone);
  const centro = (
    <Card className="gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground">{d.key}</span>
        <Link
          to={`/companies/${d.company_id}/show`}
          className="text-xl font-extrabold hover:text-primary"
        >
          {d.company_name}
        </Link>
        <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold">
          <OfferDot color={offerColor(offer ?? { slug: d.offer_slug })} />
          {d.offer_name}
        </span>
        <StagePill stage={d.stage} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xl font-semibold">
          {phone
            ? phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3")
            : "Sin teléfono"}
        </span>
        {phone &&
          (isMobile ? (
            <Button asChild>
              <a href={tel!} onClick={() => startCallTimer()}>
                <Phone />
                Llamar
              </a>
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() =>
                navigator.clipboard
                  .writeText(phone)
                  .then(() => notify("Número copiado", { type: "info" }))
              }
            >
              <Copy />
              Copiar
            </Button>
          ))}
        {phone && (
          <SendPreview
            channel="whatsapp"
            target={waTarget}
            phone={d.phone}
            trigger={
              <Button variant="outline">
                <MessageCircle />
                WhatsApp
              </Button>
            }
          />
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Pregunta por:{" "}
        {d.first_name && d.first_name !== "Encargado"
          ? `${d.first_name} ${d.last_name ?? ""}`
          : play.dec}{" "}
        · mejor horario: {bestHour(d.sector)}
      </p>
      <div className="grid gap-3 text-sm">
        {script?.sections.length ? (
          script.sections.map((s, idx) => (
            <Step key={s.title} n={idx + 1} title={s.title}>
              <span className="whitespace-pre-line">{s.body}</span>
            </Step>
          ))
        ) : (
          <>
            <Step n={1} title="Apertura">
              Hola, soy {me}, de Raftel. Le llamo por algo muy puntual. ¿Me
              regala 30 segundos y usted decide si seguimos?
            </Step>
            <Step n={2} title="Motivo">
              {ev}. {play.pitch} ¿Le puedo hacer dos preguntas rápidas?
            </Step>
            <div>
              <b>3. Preguntas</b>
              <ol className="mt-1 grid list-decimal gap-1 pl-5">
                {play.spin.map(([step, q]) => (
                  <li key={step}>{q}</li>
                ))}
              </ol>
            </div>
            <Step n={4} title="Cierre">
              ¿Le queda mejor el martes a las 10 o el jueves a las 5?
              {agenda ? (
                <>
                  {" "}
                  Si prefiere elegir él: «Le mando mi agenda para que escoja»{" "}
                  <a
                    href={agenda}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-primary underline"
                  >
                    {agenda.replace(/^https?:\/\//, "")}
                  </a>
                </>
              ) : (
                <span className="text-muted-foreground">
                  {" "}
                  (Pon tu liga de agenda en IA y ajustes para mandarla aquí.)
                </span>
              )}
            </Step>
          </>
        )}
      </div>
    </Card>
  );

  const registro = (
    <Card className="gap-3 p-4">
      <h3 className="inline-flex items-center gap-1 font-extrabold">
        Registro
        <HelpTooltip topic="calls.outcome" />
      </h3>
      {timer.active && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-mono font-semibold">
            {timer.paused ? "Pausado " : ""}
            {fmtDuration(timer.seconds)}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => (timer.paused ? timer.resume() : timer.pause())}
          >
            {timer.paused ? "Reanudar" : "Pausar"}
          </Button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
        {SESSION_OUTCOMES.map((o) => (
          <Button
            key={o.value}
            variant={outcome === o.value ? "default" : "outline"}
            className="h-11 justify-start whitespace-normal leading-tight"
            onClick={() => setOutcome(o.value)}
          >
            {o.label}
          </Button>
        ))}
      </div>
      <DictateButton
        className="self-start"
        onResult={(r) => {
          setNote((n) => (n ? `${n} ${r.note}` : r.note));
          if (r.outcome && SESSION_OUTCOMES.some((o) => o.value === r.outcome))
            setOutcome(r.outcome);
          if (
            r.nextDays != null &&
            SESSION_NEXT.some((x) => x.days === r.nextDays)
          )
            setNext(r.nextDays);
        }}
      />
      <Textarea
        placeholder="Nota"
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        {SESSION_NEXT.map((n) => (
          <Button
            key={n.days}
            size="sm"
            variant={next === n.days ? "default" : "outline"}
            className="rounded-full"
            onClick={() => setNext(n.days)}
          >
            {n.label}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {COMMITMENTS.map((c) => (
          <Button
            key={c.value}
            size="sm"
            variant={commitment === c.value ? "default" : "outline"}
            className="rounded-full"
            onClick={() =>
              setCommitment(commitment === c.value ? null : c.value)
            }
          >
            {c.label}
          </Button>
        ))}
      </div>
      <Button className="h-12" onClick={trySave} disabled={saving}>
        {saving ? "Guardando…" : "Guardar y siguiente"}
      </Button>
      <Button
        variant="outline"
        onClick={() => go(i + 1, { ...results, [d.deal_id]: "Saltada" })}
      >
        Saltar
      </Button>
    </Card>
  );

  return (
    <div className="flex flex-col gap-3">
      {head}
      <TranscriptConsentCard
        key={`consent-${d.deal_id}`}
        dealId={d.deal_id}
        contactId={d.contact_id}
        companyId={d.company_id}
        callSessionId={state.id || null}
        salesId={(identity?.id as number) ?? null}
        remitente={me}
        context={{
          offerName: d.offer_name,
          contactName: d.first_name
            ? `${d.first_name} ${d.last_name ?? ""}`.trim()
            : null,
          companyName: d.company_name,
        }}
      />
      <AudioCaptureCard key={`audio-${d.deal_id}`} dealId={d.deal_id} />
      {isMobile ? (
        <>
          {centro}
          {registro}
          {fila}
        </>
      ) : (
        <div className="grid grid-cols-[260px_minmax(0,1fr)_280px] items-start gap-3">
          {fila}
          {centro}
          {registro}
        </div>
      )}
      <LossDialog
        target={loss}
        onClose={(lost) => {
          setLoss(null);
          if (lost) save();
        }}
      />
    </div>
  );
};

const Step = ({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) => (
  <div>
    <b>
      {n}. {title}
    </b>
    <p className="mt-1 rounded-r-lg border-l-4 border-[var(--ai)] bg-secondary px-3 py-2">
      {children}
    </p>
  </div>
);
